import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, TextField, Button, Paper, Chip, Divider,
    Select, MenuItem, FormControl, InputLabel, CircularProgress,
    Alert, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LicenseService } from '../../sdk/licenseService';
import { FirestoreService } from '../../sdk/firestoreService';
import { computeLicenseStatus, computeDowngradeImpact, isUnlimited } from '../../utils/licenseUtils';
import { selectPlanDefinitions } from '../../store/actions/licenseActions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { useEffect } from 'react';

const PLANS = ['trial', 'basic', 'pro', 'community'];
const STATUS_OPTIONS = ['active', 'expired', 'cancelled'];

const PLAN_LABELS = { trial: 'Trial', basic: 'נֵר', pro: 'מְנוֹרָה', community: 'מָאוֹר' };

const formatDate = (val) => {
    if (!val) return '—';
    const d = val?.toDate ? val.toDate() : new Date(val);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const LicenseManager = () => {
    const [authUser, setAuthUser] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [license, setLicense] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newPlanForCreate, setNewPlanForCreate] = useState('community');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
    const [pendingDowngradeImpact, setPendingDowngradeImpact] = useState(null);

    const planDefinitions = useSelector(selectPlanDefinitions);

    // Editable fields
    const [plan, setPlan] = useState('');
    const [status, setStatus] = useState('');
    const [notes, setNotes] = useState('');
    const [trialEndsAt, setTrialEndsAt] = useState('');
    const [limitScreens, setLimitScreens] = useState('');
    const [limitWidgets, setLimitWidgets] = useState('');
    const [limitVersions, setLimitVersions] = useState('');
    const [allowWidgets, setAllowWidgets] = useState('');
    const [blockWidgets, setBlockWidgets] = useState('');

    const defaultTrialEnd = () => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().split('T')[0];
    };

    const toDateInput = (val) => {
        if (!val) return '';
        const d = val?.toDate ? val.toDate() : new Date(val);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
    };

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, u => setAuthUser(u || null));
        return () => unsub();
    }, []);

    const handleLoad = async () => {
        if (!orgId.trim()) return;
        setLoading(true);
        setError('');
        setLicense(null);
        setNotFound(false);
        setSuccessMsg('');
        try {
            const raw = await LicenseService.getLicense(orgId.trim());
            if (!raw) {
                setNotFound(true);
            } else {
                const computed = computeLicenseStatus(raw);
                setLicense(computed);
                setPlan(computed.plan || 'trial');
                setStatus(computed.status || 'active');
                setNotes(computed.notes || '');
                setTrialEndsAt(toDateInput(computed.trialEndsAt) || (computed.plan === 'trial' ? defaultTrialEnd() : ''));
                setLimitScreens(computed.overrides?.limits?.screens ?? '');
                setLimitWidgets(computed.overrides?.limits?.savedWidgets ?? '');
                setLimitVersions(computed.overrides?.limits?.versionHistory ?? '');
                setAllowWidgets((computed.overrides?.widgetOverrides?.allow || []).join(', '));
                setBlockWidgets((computed.overrides?.widgetOverrides?.block || []).join(', '));
            }
        } catch (e) {
            setError('שגיאה בטעינת רישיון: ' + e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLicense = async () => {
        if (!orgId.trim() || !authUser) return;
        setCreating(true);
        setError('');
        setSuccessMsg('');
        try {
            let raw;
            if (newPlanForCreate === 'trial') {
                raw = await LicenseService.createTrialLicense(orgId.trim(), authUser.uid);
            } else {
                const { doc, setDoc } = await import('firebase/firestore');
                const { db } = await import('../../firebase');
                const now = new Date();
                const licenseData = {
                    organizationId: orgId.trim(),
                    plan: newPlanForCreate,
                    status: 'active',
                    trialStartedAt: null,
                    trialEndsAt: null,
                    currentPeriodEnd: null,
                    overrides: { limits: {}, widgetOverrides: { allow: [], block: [] } },
                    createdAt: now,
                    updatedAt: now,
                    updatedBy: authUser.uid,
                    notes: ''
                };
                await setDoc(doc(db, 'licenses', orgId.trim()), licenseData);
                raw = licenseData;
            }
            const computed = computeLicenseStatus(raw);
            setLicense(computed);
            setPlan(computed?.plan || newPlanForCreate);
            setStatus(computed?.status || 'active');
            setNotes('');
            setLimitScreens('');
            setLimitWidgets('');
            setLimitVersions('');
            setAllowWidgets('');
            setBlockWidgets('');
            setNotFound(false);
            setSuccessMsg(`רישיון ${PLAN_LABELS[newPlanForCreate]} נוצר בהצלחה!`);
        } catch (e) {
            setError('שגיאה ביצירת רישיון: ' + e.message);
        } finally {
            setCreating(false);
        }
    };

    const buildUpdates = () => {
        const parseList = (str) => str.split(',').map(s => s.trim()).filter(Boolean);
        const overrideLimits = {};
        if (limitScreens !== '') overrideLimits.screens = Number(limitScreens);
        if (limitWidgets !== '') overrideLimits.savedWidgets = Number(limitWidgets);
        if (limitVersions !== '') overrideLimits.versionHistory = Number(limitVersions);
        const updates = {
            plan,
            status,
            notes,
            overrides: {
                limits: overrideLimits,
                widgetOverrides: {
                    allow: parseList(allowWidgets),
                    block: parseList(blockWidgets),
                }
            }
        };
        if (plan === 'trial') {
            const endDate = trialEndsAt ? new Date(trialEndsAt) : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
            updates.trialEndsAt = endDate;
            if (!license.trialStartedAt) updates.trialStartedAt = new Date();
        }
        return updates;
    };

    const executeSave = async () => {
        if (!license || !authUser) return;
        setSaving(true);
        setError('');
        setSuccessMsg('');
        setDowngradeDialogOpen(false);
        setPendingDowngradeImpact(null);
        try {
            const updates = buildUpdates();
            await LicenseService.updateLicense(orgId.trim(), updates, authUser.uid);
            // mirror currentPlan onto the organization doc so public display can read it
            await FirestoreService.updateOrganization(orgId.trim(), { currentPlan: plan });
            setSuccessMsg('הרישיון עודכן בהצלחה!');
            setLicense(prev => ({ ...prev, ...updates }));
        } catch (e) {
            setError('שגיאה בשמירה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => {
        if (!license || !authUser) return;
        const impact = computeDowngradeImpact(planDefinitions, license.plan, plan);
        if (impact) {
            setPendingDowngradeImpact(impact);
            setDowngradeDialogOpen(true);
        } else {
            executeSave();
        }
    };

    const handleQuickAction = async (action) => {
        if (!license || !authUser) return;
        setSaving(true);
        setError('');
        setSuccessMsg('');
        try {
            let updates = {};
            if (action === 'extend7') {
                const currentEnd = license.trialEndsAt?.toDate ? license.trialEndsAt.toDate() : new Date(license.trialEndsAt || Date.now());
                updates = { trialEndsAt: new Date(currentEnd.getTime() + 7 * 24 * 60 * 60 * 1000) };
            } else if (action === 'upgradePro') {
                updates = { plan: 'pro', status: 'active' };
                setPlan('pro');
                setStatus('active');
            } else if (action === 'suspend') {
                updates = { status: 'cancelled' };
                setStatus('cancelled');
            }
            await LicenseService.updateLicense(orgId.trim(), updates, authUser.uid);
            setLicense(prev => ({ ...prev, ...updates }));
            setSuccessMsg('בוצע!');
        } catch (e) {
            setError('שגיאה: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Typography variant="h6" fontWeight={700} color="#1e3a5f" mb={3}>
                ניהול רישיונות לקוחות
            </Typography>

            {/* Search */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
                <Typography fontWeight={600} mb={1.5} fontSize="0.9rem">חיפוש לפי Organization ID</Typography>
                <Stack direction="row" gap={1.5} alignItems="flex-start">
                    <TextField
                        size="small"
                        placeholder="הכנס organizationId..."
                        value={orgId}
                        onChange={e => setOrgId(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleLoad()}
                        sx={{ flex: 1, direction: 'ltr', '& input': { textAlign: 'left', fontFamily: 'monospace' } }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleLoad}
                        disabled={loading || !orgId.trim()}
                        sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, minWidth: 90 }}
                    >
                        {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'טען'}
                    </Button>
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

            {/* Not found — create new license */}
            {notFound && (
                <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '2px dashed #e2e8f0' }}>
                    <Typography fontWeight={700} color="#1e3a5f" mb={0.5} fontSize="0.95rem">
                        לארגון זה אין רישיון עדיין
                    </Typography>
                    <Typography fontSize="0.82rem" color="#64748b" mb={2.5}>
                        בחר מסלול וצור רישיון חדש
                    </Typography>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>מסלול</InputLabel>
                            <Select value={newPlanForCreate} onChange={e => setNewPlanForCreate(e.target.value)} label="מסלול">
                                {PLANS.map(p => <MenuItem key={p} value={p}>{PLAN_LABELS[p]}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={handleCreateLicense}
                            disabled={creating}
                            sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, textTransform: 'none', minWidth: 130 }}
                        >
                            {creating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'צור רישיון'}
                        </Button>
                    </Stack>
                </Paper>
            )}

            {/* Downgrade Impact Confirmation Dialog */}
            <Dialog open={downgradeDialogOpen} onClose={() => !saving && setDowngradeDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b45309' }}>
                    <WarningAmberIcon sx={{ color: '#f59e0b' }} />
                    אזהרה: מעבר לתוכנית נמוכה יותר
                </DialogTitle>
                <DialogContent>
                    {pendingDowngradeImpact && (
                        <Stack gap={2}>
                            <Typography fontSize="0.9rem" color="#64748b">
                                מעבר מ-<strong>{PLAN_LABELS[pendingDowngradeImpact.fromPlan]}</strong> ל-<strong>{PLAN_LABELS[pendingDowngradeImpact.toPlan]}</strong>:
                            </Typography>
                            <Box sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', p: 2 }}>
                                <Stack gap={1}>
                                    {!isUnlimited(pendingDowngradeImpact.limits.screens.to) && (
                                        <Typography fontSize="0.85rem">
                                            • <strong>מסכים:</strong> מגבלה חדשה {pendingDowngradeImpact.limits.screens.to} — מסכים עודפים יושהו (הדיספליי יכבה)
                                        </Typography>
                                    )}
                                    {!isUnlimited(pendingDowngradeImpact.limits.savedWidgets.to) && (
                                        <Typography fontSize="0.85rem">
                                            • <strong>תבניות שמורות:</strong> מגבלה חדשה {pendingDowngradeImpact.limits.savedWidgets.to === 0 ? 'ללא תבניות' : pendingDowngradeImpact.limits.savedWidgets.to}
                                        </Typography>
                                    )}
                                    {!isUnlimited(pendingDowngradeImpact.limits.versionHistory.to) && (
                                        <Typography fontSize="0.85rem">
                                            • <strong>גרסאות לדשבורד:</strong> מגבלה חדשה {pendingDowngradeImpact.limits.versionHistory.to}
                                        </Typography>
                                    )}
                                    <Typography fontSize="0.85rem">
                                        • <strong>רכיבים פרמיום</strong> בדשבורדים יוצגו כ-Placeholder על המסך
                                    </Typography>
                                </Stack>
                            </Box>
                            <Typography fontSize="0.82rem" color="#64748b">
                                הנתונים לא יימחקו. שדרוג בחזרה יחזיר הכל לפעולה.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDowngradeDialogOpen(false)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
                        ביטול
                    </Button>
                    <Button
                        variant="contained"
                        onClick={executeSave}
                        disabled={saving}
                        sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' }, textTransform: 'none' }}
                    >
                        {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'בצע מעבר'}
                    </Button>
                </DialogActions>
            </Dialog>

            {license && (
                <Stack gap={3}>
                    {/* Status summary */}
                    <Paper sx={{ p: 3, borderRadius: '12px' }}>
                        <Typography fontWeight={700} mb={2} fontSize="0.95rem">סטטוס נוכחי</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={2}>
                            <Box>
                                <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>מסלול</Typography>
                                <Chip label={PLAN_LABELS[license.plan] || license.plan} size="small"
                                    sx={{ bgcolor: '#1e3a5f', color: '#fff', fontWeight: 700 }} />
                            </Box>
                            <Box>
                                <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>סטטוס</Typography>
                                <Chip label={license.status} size="small"
                                    sx={{ bgcolor: license.isExpired ? '#fee2e2' : '#dcfce7', color: license.isExpired ? '#b91c1c' : '#15803d', fontWeight: 600 }} />
                            </Box>
                            {license.plan === 'trial' && (
                                <Box>
                                    <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>ניסיון מסתיים</Typography>
                                    <Typography fontSize="0.85rem" fontWeight={600}>{formatDate(license.trialEndsAt)}</Typography>
                                </Box>
                            )}
                            {license.daysRemainingInTrial !== null && license.daysRemainingInTrial !== undefined && (
                                <Box>
                                    <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>ימים שנותרו</Typography>
                                    <Typography fontSize="0.85rem" fontWeight={600} color={license.daysRemainingInTrial <= 3 ? '#dc2626' : '#1e3a5f'}>
                                        {license.daysRemainingInTrial}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>נוצר ב</Typography>
                                <Typography fontSize="0.85rem">{formatDate(license.createdAt)}</Typography>
                            </Box>
                            <Box>
                                <Typography fontSize="0.72rem" color="#94a3b8" mb={0.25}>עודכן ב</Typography>
                                <Typography fontSize="0.85rem">{formatDate(license.updatedAt)}</Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Quick actions */}
                        <Typography fontWeight={600} mb={1.5} fontSize="0.85rem">פעולות מהירות</Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            <Button size="small" variant="outlined" onClick={() => handleQuickAction('extend7')} disabled={saving}
                                sx={{ borderColor: '#e2e8f0', color: '#475569', textTransform: 'none', fontSize: '0.8rem' }}>
                                +7 ימי ניסיון
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => handleQuickAction('upgradePro')} disabled={saving}
                                sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', textTransform: 'none', fontSize: '0.8rem' }}>
                                שדרג ל-Pro
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => handleQuickAction('suspend')} disabled={saving}
                                sx={{ borderColor: '#ef4444', color: '#ef4444', textTransform: 'none', fontSize: '0.8rem' }}>
                                השהה
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Edit */}
                    <Paper sx={{ p: 3, borderRadius: '12px' }}>
                        <Typography fontWeight={700} mb={2} fontSize="0.95rem">עריכה</Typography>
                        <Stack gap={2}>
                            <Stack direction="row" gap={2}>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>מסלול</InputLabel>
                                    <Select value={plan} onChange={e => {
                                        setPlan(e.target.value);
                                        if (e.target.value === 'trial' && !trialEndsAt) {
                                            setTrialEndsAt(defaultTrialEnd());
                                        }
                                    }} label="מסלול">
                                        {PLANS.map(p => <MenuItem key={p} value={p}>{PLAN_LABELS[p] || p}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel>סטטוס</InputLabel>
                                    <Select value={status} onChange={e => setStatus(e.target.value)} label="סטטוס">
                                        {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Stack>

                            {/* Trial end date — מוצג רק כשמסלול trial */}
                            {plan === 'trial' && (
                                <Box>
                                    <Typography fontWeight={600} fontSize="0.82rem" color="#64748b" mb={1}>
                                        תאריך סיום ניסיון
                                    </Typography>
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={trialEndsAt}
                                        onChange={e => setTrialEndsAt(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        helperText="חובה למלא כשמסלול הוא Trial — קובע מתי הניסיון פג"
                                        sx={{ width: 220 }}
                                    />
                                </Box>
                            )}

                            <Typography fontWeight={600} fontSize="0.82rem" color="#64748b">Override מגבלות (ריק = לפי מסלול)</Typography>
                            <Stack direction="row" gap={2}>
                                <TextField size="small" label="מסכים" type="number" value={limitScreens}
                                    onChange={e => setLimitScreens(e.target.value)} sx={{ flex: 1 }} />
                                <TextField size="small" label="רכיבים שמורים" type="number" value={limitWidgets}
                                    onChange={e => setLimitWidgets(e.target.value)} sx={{ flex: 1 }} />
                                <TextField size="small" label="גרסאות" type="number" value={limitVersions}
                                    onChange={e => setLimitVersions(e.target.value)} sx={{ flex: 1 }} />
                            </Stack>

                            <Typography fontWeight={600} fontSize="0.82rem" color="#64748b">Override רכיבים (componentKeys מופרדים בפסיק)</Typography>
                            <TextField size="small" label="רכיבים מורשים (allow)" value={allowWidgets}
                                onChange={e => setAllowWidgets(e.target.value)}
                                placeholder="ParnasWidget, FinancialGaugeWidget" />
                            <TextField size="small" label="רכיבים חסומים (block)" value={blockWidgets}
                                onChange={e => setBlockWidgets(e.target.value)}
                                placeholder="NitzavSpotlightWidget" />

                            <TextField size="small" label="הערות פנימיות" value={notes}
                                onChange={e => setNotes(e.target.value)} multiline rows={2} />

                            <Button variant="contained" onClick={handleSave} disabled={saving}
                                sx={{ bgcolor: '#1e3a5f', '&:hover': { bgcolor: '#152844' }, textTransform: 'none', alignSelf: 'flex-start', px: 4 }}>
                                {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'שמור שינויים'}
                            </Button>
                        </Stack>
                    </Paper>
                </Stack>
            )}
        </Box>
    );
};

export default LicenseManager;
